import React, { useState, useEffect, useCallback } from "react";
import { dataGridSurface } from "../common/tableStyles";
import { Box, styled, Chip, Alert, Button } from "@mui/material";
import { DataGrid, GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import CollectionService from "../../services/CollectionService";
import ICollection from "../../entities/ICollection";
import CollectionActions, { isCollectionActive } from "./CollectionActions";
import { unwrapList, getErrorMessage } from "../../services/apiUtils";
import { promoDateToMs } from "../../services/textUtils";
import DataGridLoadingOverlay from "../common/DataGridLoadingOverlay";




const formatCollectionDate = (val: string | number | null | undefined): string => {
  const ms = promoDateToMs(val);
  return ms === null ? "—" : new Date(ms).toLocaleString();
};


const StyledTable = styled(Box)(({ theme }) => ({
  ...dataGridSurface(theme),
  margin: "20px 0 0 0",
  height: 560,
}));

const getRowId = (row: any) => row.collectionId ?? row.id;

const ListCollection: React.FC = () => {
  const [collectionData, setCollectionData] = useState<Array<ICollection>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

  const loadCollections = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    CollectionService.getCollections()
      .then((res: any) => {
        setCollectionData(unwrapList<ICollection>(res));
      })
      .catch((err: any) => {
        setCollectionData([]);
        setLoadError(getErrorMessage(err, "Could not load collections."));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const columns: GridColDef[] = [
    { field: "collectionId", headerName: "ID", valueGetter: (params) => getRowId(params.row) },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "slug",
      headerName: "Slug",
      type: "string",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "productCount",
      headerName: "Products",
      sortable: false,
      filterable: false,
      renderCell: (params) => {

        const products =
          params.row.collectionProducts ||
          params.row.collectionProductRequestDtos ||
          params.row.collectionProductList ||
          [];
        return <Chip size="small" label={products.length} />;
      },
    },
    {
      field: "statusId",
      headerName: "Status",
      renderCell: (params) => {
        const savedActive = isCollectionActive(params.row);
        const expired = CollectionService.isCollectionExpired(params.row);
        const upcoming = CollectionService.isCollectionUpcoming(params.row);

        if (expired) {
          return <Chip size="small" label="Expired" color="default" />;
        }
        if (upcoming) {
          return <Chip size="small" label="Scheduled" color="info" />;
        }
        return (
          <Chip
            size="small"
            label={savedActive ? "Active" : "Inactive"}
            color={savedActive ? "success" : "default"}
          />
        );
      },
    },
    {
      field: "starts_at",
      headerName: "Starts",
      flex: 1,
      renderCell: (params) => formatCollectionDate(params.row.starts_at),
    },
    {
      field: "ends_at",
      headerName: "Ends",
      flex: 1,
      renderCell: (params) => formatCollectionDate(params.row.ends_at),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 140,
      type: "actions",
      renderCell: (params) => (
        <CollectionActions
          collection={params.row}
          onDeleted={loadCollections}
          onStatusChanged={loadCollections}
        />
      ),
    },
  ];

  return (
    <Box>
      {loadError && (
        <Alert
          severity="error"
          sx={{ mt: "20px" }}
          action={
            <Button color="inherit" size="small" onClick={loadCollections}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      <StyledTable>
        <DataGrid
          sx={{ backgroundColor: "transparent" }}
          rows={collectionData}
          columns={columns}
          getRowId={getRowId}
          components={{ LoadingOverlay: DataGridLoadingOverlay }}
          loading={loading}
          pageSize={30}
          rowsPerPageOptions={[30]}
          checkboxSelection
          disableSelectionOnClick
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
        />
      </StyledTable>
    </Box>
  );
};

export default ListCollection;
